import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { type DropzoneFileItem } from "@platform/design-system";
import { logger } from "@platform/browser-logger";
import { useNotification } from "@platform/hooks";
import { useQueryClient } from "@tanstack/react-query";

import { AddDocumentsDialog } from "../components/AddDocumentsDialog";
import {
  useError,
  useProcessDocumentMutation,
  useUploadDocumentsMutation,
} from "../hooks";
import { getPlanBuilderDataCacheKey } from "../hooks/usePlanBuilderData";

import type { UploadedDocument } from "../types";

export type AddDocumentsDialogReturn = {
  isOpen: boolean;
  files: DropzoneFileItem[];
  uploadedDocuments: UploadedDocument[];
  canUpload: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  handleUploadFiles: (acceptedFiles: File[]) => void;
  handleDeleteFile: (fileToDelete: DropzoneFileItem) => void;
  handleDeleteDocument: (id: string) => void;
  handleDocumentChange: <K extends keyof UploadedDocument>(
    id: string,
    field: K,
    value: UploadedDocument[K],
  ) => void;
  handleUpload: () => void;
  isUploading: boolean;
};

const AddDocumentsDialogContext = createContext<
  AddDocumentsDialogReturn | undefined
>(undefined);

export { AddDocumentsDialogContext };

type AddDocumentsDialogProviderProps = {
  rfpId: string;
  brokerId?: number;
  brokerPortalRfpId?: number;
  carriers: string[];
  planEffectiveDate?: string;
  children: ReactNode;
};

export const AddDocumentsDialogProvider = ({
  rfpId,
  brokerId,
  brokerPortalRfpId,
  carriers,
  planEffectiveDate,
  children,
}: AddDocumentsDialogProviderProps): React.JSX.Element => {
  const { setError } = useError();
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();
  const { mutateAsync: uploadDocuments, isPending: isUploading } =
    useUploadDocumentsMutation();
  const { mutateAsync: processDocumentAsync } = useProcessDocumentMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<DropzoneFileItem[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocument[]
  >([]);

  const openDialog = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const resetDialogState = useCallback((): void => {
    setFiles([]);
    setUploadedDocuments([]);
  }, []);

  const cancelDialog = useCallback((): void => {
    closeDialog();
    resetDialogState();
  }, [closeDialog, resetDialogState]);

  const handleUploadFiles = useCallback((acceptedFiles: File[]): void => {
    const mappedFiles: DropzoneFileItem[] = acceptedFiles.map((file) => ({
      name: file.name,
      downloadLink: "",
      size: file.size,
      infected: false,
      type: file.type,
    }));

    setFiles((prev) => [...prev, ...mappedFiles]);

    const newDocs: UploadedDocument[] = acceptedFiles.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      file,
      documentType: null,
      assignedCoverage: [],
      assignedCarrier: null,
    }));
    setUploadedDocuments((prev: UploadedDocument[]) => [...prev, ...newDocs]);
  }, []);

  const handleDeleteFile = useCallback(
    (fileToDelete: DropzoneFileItem): void => {
      setFiles((prev) =>
        prev.filter((file) => file.name !== fileToDelete.name),
      );
    },
    [],
  );

  const handleDeleteDocument = useCallback(
    (id: string): void => {
      const removedDocumentName =
        uploadedDocuments.find((doc) => doc.id === id)?.name ?? null;
      if (!removedDocumentName) {
        return;
      }
      setUploadedDocuments((prev: UploadedDocument[]) =>
        prev.filter((doc) => doc.id !== id),
      );
      setFiles((prev) =>
        prev.filter((file) => file.name !== removedDocumentName),
      );
    },
    [uploadedDocuments],
  );

  const handleDocumentChange = useCallback(
    <K extends keyof UploadedDocument>(
      id: string,
      field: K,
      value: UploadedDocument[K],
    ): void => {
      setUploadedDocuments((prev: UploadedDocument[]) =>
        prev.map((doc): UploadedDocument => {
          if (doc.id !== id) {
            return doc;
          }

          return { ...doc, [field]: value } as UploadedDocument;
        }),
      );
    },
    [],
  );

  const isUnassigned = useCallback(
    (doc: UploadedDocument): boolean =>
      !doc.documentType ||
      (doc.assignedCoverage?.length ?? 0) === 0 ||
      !doc.assignedCarrier,
    [],
  );

  const canUpload = useMemo(
    () =>
      uploadedDocuments.length > 0 &&
      uploadedDocuments.every((doc) => !isUnassigned(doc)),
    [uploadedDocuments, isUnassigned],
  );

  const handleUpload = useCallback((): void => {
    logger.debug("[Document Library] Uploading documents", {
      documentsCount: uploadedDocuments.length,
      rfpId,
    });

    if (
      !Number.isFinite(brokerId) ||
      !Number.isFinite(brokerPortalRfpId) ||
      brokerId === undefined ||
      brokerPortalRfpId === undefined
    ) {
      const err = new Error(
        "Cannot upload documents: brokerId or brokerPortalRfpId is not set",
      );
      setError("upload", err);
      logger.error(`[Document Library] ${err.message}`, err);
      return;
    }

    uploadDocuments({
      rfpId,
      brokerId,
      brokerPortalRfpId,
      documents: uploadedDocuments,
    })
      .then(async (response) => {
        setError("upload", null);
        showSuccess("Documents uploaded successfully");

        // Close the dialog immediately so the user isn't blocked while
        // process dispatch and cache invalidation happen in the background.
        closeDialog();
        resetDialogState();

        await Promise.allSettled(
          response.documents.map((doc) =>
            processDocumentAsync({
              rfpId,
              documentId: doc.id,
              planEffectiveDate,
            }).catch((error) => {
              logger.error(
                `[Document Library] Failed to dispatch process for documentId=${doc.id}`,
                error,
              );
            }),
          ),
        );

        await queryClient.invalidateQueries({
          queryKey: getPlanBuilderDataCacheKey(rfpId),
        });
        logger.info("[Document Library] Documents uploaded successfully", {
          rfpId,
        });
      })
      .catch((error: Error) => {
        closeDialog();
        setError("upload", error);
        logger.error(
          `[Document Library] Upload failed for rfpId=${rfpId}`,
          error,
        );
      });
  }, [
    closeDialog,
    brokerId,
    brokerPortalRfpId,
    planEffectiveDate,
    processDocumentAsync,
    queryClient,
    rfpId,
    resetDialogState,
    setError,
    showSuccess,
    uploadDocuments,
    uploadedDocuments,
  ]);

  const value = useMemo(
    () => ({
      isOpen,
      files,
      uploadedDocuments,
      canUpload,
      openDialog,
      closeDialog,
      handleUploadFiles,
      handleDeleteFile,
      handleDeleteDocument,
      handleDocumentChange,
      handleUpload,
      isUploading,
    }),
    [
      isOpen,
      files,
      uploadedDocuments,
      canUpload,
      openDialog,
      closeDialog,
      handleUploadFiles,
      handleDeleteFile,
      handleDeleteDocument,
      handleDocumentChange,
      handleUpload,
      isUploading,
    ],
  );

  return (
    <AddDocumentsDialogContext.Provider value={value}>
      {children}
      <AddDocumentsDialog
        open={isOpen}
        onClose={cancelDialog}
        files={files}
        uploadedDocuments={uploadedDocuments}
        canUpload={canUpload}
        carriers={carriers}
        handleUploadFiles={handleUploadFiles}
        handleDeleteFile={handleDeleteFile}
        handleDeleteDocument={handleDeleteDocument}
        handleDocumentChange={handleDocumentChange}
        handleUpload={handleUpload}
        isUploading={isUploading}
      />
    </AddDocumentsDialogContext.Provider>
  );
};
